import React from "react";
import { 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  CircleDashed, 
  SkipForward, 
  XCircle, 
  MoreVertical 
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  INSTALLATION_STATUS_LABELS,
  INSTALLATION_STATUS_BADGE_COLORS,
  InstallationStatus,
} from "@/config/constants";
import { getCustomerDisplayName } from "@/lib/formatters";
import { LoadInstallation } from "@/types/loads";
import { 
  isHandledStatus, 
  getLoadPositionColor, 
  getInstallationStatusIconType,
  StatusIconType 
} from "@/lib/load-helpers";

interface LoadInstallationItemProps {
  installation: LoadInstallation;
  index: number;
  isExpanded: boolean;
  isHighlighted: boolean;
  onToggleExpand: (id: string) => void;
  onStatusChange: (id: string, status: InstallationStatus) => void;
}

// Map icon type to component
const StatusIcon = ({ type }: { type: StatusIconType }) => {
  switch (type) {
    case "check": return <CheckCircle2 className="h-3 w-3" />;
    case "dashed": return <CircleDashed className="h-3 w-3" />;
    case "skip": return <SkipForward className="h-3 w-3" />;
    case "x": return <XCircle className="h-3 w-3" />;
    default: return null;
  }
};

export const LoadInstallationItem = React.memo(function LoadInstallationItem({
  installation: inst,
  index,
  isExpanded,
  isHighlighted,
  onToggleExpand,
  onStatusChange,
}: LoadInstallationItemProps) {
  const orderLines = inst.order?.order_lines || [];
  const instStatus = inst.status as InstallationStatus;
  const isHandled = isHandledStatus(instStatus);
  const positionColor = getLoadPositionColor(instStatus);
  const statusIconType = getInstallationStatusIconType(instStatus);

  return (
    <Collapsible 
      open={isExpanded} 
      onOpenChange={() => onToggleExpand(inst.id)}
    >
      <div 
        id={`admin-task-${inst.id}`}
        className={`rounded-md bg-muted/50 overflow-hidden transition-all duration-300 ${isHandled && instStatus !== "completed" ? "opacity-70" : ""} ${isHighlighted ? "ring-2 ring-primary ring-offset-2 bg-primary/10" : ""}`}
      >
        <CollapsibleTrigger asChild>
          <div className="flex items-center gap-3 p-2 cursor-pointer hover:bg-muted/80 transition-colors">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${positionColor}`}>
              {isHandled ? <StatusIcon type={statusIconType} /> : (inst.load_position || index + 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">
                {inst.order?.order_number} - {getCustomerDisplayName(inst.order?.customer)}
              </div>
              <div className="text-xs text-muted-foreground">
                {inst.cemetery?.name}, {inst.cemetery?.city}
              </div>
              {inst.notes && isHandled && (
                <div className="text-xs text-muted-foreground italic mt-0.5">
                  {inst.notes}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isHandled && (
                <Badge className={`text-xs ${INSTALLATION_STATUS_BADGE_COLORS[instStatus]}`}>
                  {INSTALLATION_STATUS_LABELS[instStatus]}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {inst.task_type === "installation" ? "Asennus" : "Nouto"}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem 
                    onClick={() => onStatusChange(inst.id, "completed")}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                    Valmis
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onStatusChange(inst.id, "partial")}
                  >
                    <CircleDashed className="h-4 w-4 mr-2 text-orange-600" />
                    Osittain valmis
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onStatusChange(inst.id, "skipped")}
                  >
                    <SkipForward className="h-4 w-4 mr-2 text-red-600" />
                    Ohitettu
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onStatusChange(inst.id, "pending")}
                  >
                    Palauta odottavaksi
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-3 pt-1 border-t border-border/50">
            {orderLines.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">Ei tuotteita</p>
            ) : (
              <div className="space-y-2 pt-2">
                {orderLines.map((line) => {
                  const config = line.configuration;
                  const isConfigured = !!config;
                  
                  return (
                    <div 
                      key={line.id} 
                      className={`text-sm ${isConfigured ? 'bg-muted/50 rounded-md p-2' : ''}`}
                    >
                      {isConfigured ? (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-medium flex items-center gap-1.5">
                              <span className="text-primary">🔧</span>
                              {config.product_model?.name || "Konfiguroitu tuote"}
                            </span>
                            <span className="text-muted-foreground">
                              {line.quantity} kpl
                            </span>
                          </div>
                          {config.config_components && config.config_components.length > 0 && (() => {
                            const activeComponents = config.config_components
                              .filter((comp) => comp.quantity > 0)
                              .sort((a, b) => (a.model_component?.position ?? 999) - (b.model_component?.position ?? 999));

                            if (activeComponents.length === 0) return null;

                            // Group by source_model_name
                            const groups = new Map<string, typeof activeComponents>();
                            activeComponents.forEach((comp) => {
                              const key = comp.source_model_name || "";
                              if (!groups.has(key)) groups.set(key, []);
                              groups.get(key)!.push(comp);
                            });

                            return (
                              <div className="text-xs text-muted-foreground pl-8 space-y-1">
                                {Array.from(groups.entries()).map(([groupName, comps], idx) => (
                                  <div key={groupName || "_default"} className={`space-y-0.5 ${groupName ? "border-l-2 border-blue-200 pl-2" : ""} ${idx > 0 ? "mt-2" : ""}`}>
                                    {groupName && (
                                      <span className="inline-block bg-blue-50 text-blue-700 text-[10px] font-medium px-1.5 py-0.5 rounded">
                                        {groupName}
                                      </span>
                                    )}
                                    {comps.map((comp) => {
                                      const compName = comp.model_component?.component_type?.label || 
                                                     comp.model_component?.component_type?.name || "Komponentti";
                                      const materialName = comp.material?.name;
                                      const dims = [comp.width, comp.height, comp.thickness]
                                        .filter(d => d != null && d > 0)
                                        .map(d => Math.round((d as number) * 1000))
                                        .join('×');
                                      
                                      return (
                                        <div key={comp.id} className="flex items-center gap-1">
                                          <span>•</span>
                                          <span>{compName}</span>
                                          {materialName && <span className="font-medium">: {materialName}</span>}
                                          {dims && <span>({dims} mm)</span>}
                                          {comp.quantity > 1 && <span>× {comp.quantity}</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-foreground">
                            {line.product?.name || line.description}
                          </span>
                          <span className="text-muted-foreground">
                            {line.quantity} kpl
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
});
